import React, {Component} from 'react'
import {connect} from 'react-redux'
import NaviForm from 'employees/NaviForm'
import {formName, withFormSelectors, withFormSelectorsProvider} from 'employees/form/common'
import {makeFormValues} from 'employees/Form'
import withRouterLegacy from 'withRouterLegacy'
import {actionCreators} from 'actions'
import api from 'api'
import Button from 'jbc-front/components/Button'
import {employeeDetailEmbed} from 'employees/Show'
import {fetchSelector} from 'actions'
import {get, isEmpty, snakeCase} from 'lodash'
import unset from 'lodash/fp/unset'
import {getState, isAdminSelector} from 'utils'
import {toFormData} from 'api/postWithFile'
import Loading from 'components/Loading'
import {updateEmployeeDetail} from 'employees/Update'
import {FieldGroupProvider} from 'employees/FieldWithGroup'
import {CommentModalWithChildren} from 'components/modals/CommentModal'
import CancelModal from 'accountSettings/CancelModal'
import {formatValues} from 'employees/utils'
import {notifySuccess} from 'store/actions/notify'
import {asyncError} from 'store/actions/asyncError'
import {eachFormFiles} from 'employees/eachFormFiles'
import {onSubmitFail} from 'jbc-front/components/FormErrors'
import {getFormValues, SubmissionError, stopSubmit} from 'redux-form'
import {processError, processResumesError} from './libs/errorHandler'
import {flow} from 'lodash/fp'

const makeFileData = (form, file, dbName) => {
  if (file instanceof File) {
    form.push([`files[${dbName}]`, file])
  } else if (!file) {
    form.push([`files[${dbName}]`, 'removed'])
  }
}

export const makeFormData = (values, customFields, {type, comment}) => {
  const form = []
  let data = unset('resumes', formatValues(values))
  for (const file of eachFormFiles(values, customFields, {ignorePermission: true})) {
    if (file.file !== null) {
      data = unset(file.formName, data)
    }
  }

  form.push(['data', JSON.stringify(data)])

  for (const file of eachFormFiles(values, customFields)) {
    makeFileData(form, file.file, file.dbName)
  }

  form.push(['type', type])

  if (comment) {
    form.push(['comment', comment])
  }

  return toFormData(form)
}

class InitialInput extends Component {
  componentDidMount() {
    const {loadData, loadFieldGroup, token, client} = this.props
    loadData(token)
    loadFieldGroup(token, client)
  }

  handleSubmit = async (comment) => {
    const {data, fields, token, update, getFormValues} = this.props
    const values = await getFormValues(formName)
    const formValues = makeFormValues({values, hideEmploymentInfo: true})
    const type = get(data, 'detail_input_request.type')
    await update(formValues, token, fields, type, comment, this.props.history)
  }

  componentWillUnmount() {
    this.props.destroy()
  }

  render() {
    const {data, submit, submitting, inputData, fieldGroups, employeeFieldGroups, selector, isAdmin} = this.props
    if (isEmpty(data) || inputData.loading || (!inputData.data && inputData.finished === undefined)) {
      return (
        <div className="l-wrap-xs u-pt100">
          <div className="l-box-message">
            <Loading />
          </div>
        </div>
      )
    }
    if (inputData.finished) {
      return (
        <div className="l-wrap-xs u-pt100">
          <div className="l-box-message">
            <p className="m-title-sub u-pt0 u-ta-c">すでに入力済です</p>
          </div>
        </div>
      )
    }
    const {type, status} = get(data, 'detail_input_request')
    const comments = get(data, 'detail_input_request.comments')
    const isRejectedProfile = type === 'UpdateProfile' && status === 'rejected'
    const modalTitle = isAdmin ? '修正依頼' : 'その他申請'
    const placeholder = isAdmin ? '修正して欲しい内容など入力してください' : ''

    return (
      <FieldGroupProvider
        type={type}
        requestGroups={get(data, 'detail_input_request.groups')}
        permissionGroups={employeeFieldGroups}
      >
        <CommentModalWithChildren
          title={modalTitle}
          placeholder={placeholder}
          onSubmit={(formData) => this.handleSubmit(formData.comment)}
        >
          {(showModal, _isOpen) => (
            <>
              <NaviForm
                data={data}
                fieldGroups={fieldGroups}
                employeeFieldGroups={employeeFieldGroups}
                onSubmit={showModal}
                submitText={isRejectedProfile ? '修正完了' : '完了'}
                hideEmploymentInfo
                tmpData={inputData.data.data_raw}
                tmpFiles={inputData.data.detail_input_files}
                selector={selector}
                naviSubmit={
                  <Button primary disabled={submitting} onClick={() => submit()} widthWide>
                    {isRejectedProfile ? '修正完了' : '完了'}
                  </Button>
                }
                otherButtons={
                  isRejectedProfile && (
                    <CancelModal
                      id={get(data, 'id')}
                      render={({showModal}) => (
                        <Button onClick={showModal} className="u-mr20">
                          依頼を取り下げる
                        </Button>
                      )}
                    />
                  )
                }
                isInitialInput={type === 'InitialInput'}
                comments={comments}
              />
            </>
          )}
        </CommentModalWithChildren>
      </FieldGroupProvider>
    )
  }
}

const mapStateToProps = (state, {isSubmitting}) => ({
  data: state.employees.current.data,
  submitting: isSubmitting(state),
  fields: fetchSelector(state, 'custom_fields').data,
  inputData: fetchSelector(state, 'detail_input_data'),
  token: state.auth.token,
  nations: state.master.nations,
  fieldGroups: fetchSelector(state, 'custom_field_groups').data,
  employeeFieldGroups: fetchSelector(state, 'employee_field_groups').data,
  client: state.client.current,
  isAdmin: isAdminSelector(state),
})

const mapDispatchToProps = (dispatch, {submit, getValues}) => ({
  async loadData(token) {
    dispatch(
      actionCreators.employees.current.fetchData(
        api.createWithAuth(token).employees.getCurrent(employeeDetailEmbed(true))
      )
    )
    dispatch(actionCreators.fetchData('detail_input_data', api.createWithAuth(token).detailInputData.get()))
  },
  async loadFieldGroup(token, client) {
    dispatch(actionCreators.fetchData('employee_field_groups', api.createWithAuth(token).employeeFieldGroups.list()))
    dispatch(
      actionCreators.fetchData(
        'custom_field_groups',
        api.createWithAuth(token).employeeCustom.fieldGroups.list(client.id)
      )
    )
    dispatch(
      actionCreators.fetchData(
        'custom_fields',
        api.createWithAuth(token).employeeCustom.fields.list(client.id, {
          embed: ['custom_employee_field_options', 'custom_employee_field_permissions'],
        })
      )
    )
  },
  destroy() {
    dispatch(actionCreators.employees.current.destroy())
    dispatch(actionCreators.fetchDestroy('detail_input_data'))
    dispatch(actionCreators.fetchDestroy('employee_field_groups'))
    dispatch(actionCreators.fetchDestroy('custom_field_groups'))
    dispatch(actionCreators.fetchDestroy('custom_fields'))
  },
  submit() {
    dispatch(submit)
  },
  async update(values, token, fields, type, comment, history) {
    try {
      const state = await getState(dispatch)
      const formValues = getValues(state)
      await updateEmployeeDetail(values.employee.id, {resumes: values.resumes}, token, undefined, fields)
      await api
        .createWithAuth(token)
        .detailInputData.create(makeFormData(formValues, fields, {type: snakeCase(type), comment}), {
          headers: {'content-type': 'multipart/form-data'},
        })
        .catch((err) => {
          processError(err)
        })
      history.replace('/dashboard')
      dispatch(notifySuccess(type === 'InitialInput' ? '初回入力完了しました' : '送信しました'))
    } catch (err) {
      if (err instanceof SubmissionError) {
        if (Object.keys(err.errors)[0] === 'resumes') {
          err.errors = processResumesError(err.errors)
        }
        onSubmitFail(err.errors, dispatch)
        dispatch(stopSubmit(formName, err.errors))
      } else {
        dispatch(asyncError(err))
      }
    }
  },
  async getFormValues(formName) {
    const state = await getState(dispatch)
    const getValues = getFormValues(formName)
    return getValues(state)
  },
})

export default flow(
  connect(mapStateToProps, mapDispatchToProps),
  withFormSelectors,
  withFormSelectorsProvider,
  withRouterLegacy
)(InitialInput)
